/**
 * Wymiarowanie łańcuchowe w stylu projektu właściciela (`a3DACH.pdf`): linia wymiarowa z ukośnymi kreskami
 * w każdym punkcie, wartości w mm nad odcinkami, krótkie odcinki (przerwy 20 mm) z napisem uniesionym wyżej,
 * a na zewnątrz wymiar całkowity.
 *
 * Czyste funkcje bez pdf-lib: punkty w metrach wzdłuż osi, położenie napisów w mm na papierze. Szerokość napisu
 * podaje funkcja `szerokosc(tekst)` (w rysunku z czcionki, w testach z przybliżenia), więc ten sam rozkład
 * działa przy obliczaniu skali i przy rysowaniu.
 */

// Punkty bliższe niż 1,5 mm w terenie to jeden punkt: wymiary podajemy w pełnych milimetrach.
export const TOLERANCJA = 0.0015

/** Posortowane punkty bez powtórzeń; punkty bliższe niż tolerancja łączą się w pierwszy z nich. */
export const punktyLancucha = (punkty, tol = TOLERANCJA) => {
  const p = punkty.filter((x) => Number.isFinite(x)).sort((a, b) => a - b)
  const wynik = []
  for (const x of p) if (!wynik.length || x - wynik[wynik.length - 1] > tol) wynik.push(x)
  return wynik
}

/**
 * Rozkład jednego łańcucha na papierze. `naPapier(m)` zamienia punkt na położenie wzdłuż linii wymiarowej w mm.
 * Napis mieści się nad swoim odcinkiem (poziom 0) albo idzie wyżej, na pierwszy wolny poziom 1..maksPoziomow,
 * wyśrodkowany nad odcinkiem; gdy wszystkie są zajęte, na poziom 1 tuż za ostatnim napisem.
 * @returns { punkty, odcinki: [{ od, do, mm, tekst, x, poziom }], poziomy }  `x`: środek napisu w mm
 */
export const rozkladLancucha = ({ punkty, naPapier, szerokosc, odstep = 0.5, maksPoziomow = 3, tol = TOLERANCJA }) => {
  const p = punktyLancucha(punkty, tol)
  const odcinki = []
  const zajete = Array.from({ length: maksPoziomow + 1 }, () => [])
  const wolne = (poziom, lo, hi) => zajete[poziom].every(([a, b]) => hi <= a || lo >= b)
  let poziomy = 0
  for (let i = 0; i + 1 < p.length; i += 1) {
    const mm = Math.round((p[i + 1] - p[i]) * 1000)
    if (mm <= 0) continue
    const tekst = String(mm)
    const w = szerokosc(tekst)
    const a = naPapier(p[i])
    const b = naPapier(p[i + 1])
    const srodek = (a + b) / 2
    let poziom = 0
    let x = srodek
    if (w + 2 * odstep > Math.abs(b - a)) {
      poziom = 0
      for (let k = 1; k <= maksPoziomow && !poziom; k += 1) if (wolne(k, srodek - w / 2 - odstep, srodek + w / 2 + odstep)) poziom = k
      if (!poziom) {
        poziom = 1
        x = Math.max(srodek, ...zajete[1].map(([, koniec]) => koniec + w / 2 + odstep))
      }
      zajete[poziom].push([x - w / 2 - odstep, x + w / 2 + odstep])
    }
    poziomy = Math.max(poziomy, poziom)
    odcinki.push({ od: p[i], do: p[i + 1], mm, tekst, x, poziom })
  }
  return { punkty: p, odcinki, poziomy }
}

/**
 * Czy dwa zestawy punktów mogą leżeć w jednym łańcuchu: na odcinku, na którym się pokrywają, punkty jednego
 * zawierają się w punktach drugiego (ta sama siatka modułów, np. rząd z przerwą po kominie i pełny rząd),
 * albo w ogóle się nie pokrywają. Rzędy przesunięte o pół modułu i przeszkody w polu modułów są niezgodne.
 */
export const zgodnePunkty = (a, b, tol = TOLERANCJA) => {
  const lo = Math.max(a[0], b[0]) - tol
  const hi = Math.min(a[a.length - 1], b[b.length - 1]) + tol
  if (hi - lo <= 2 * tol) return true
  const wA = a.filter((x) => x >= lo && x <= hi)
  const wB = b.filter((x) => x >= lo && x <= hi)
  // Cecha w całości wewnątrz jednego odcinka drugiego łańcucha rozcięłaby jego wymiar (komin nad modułem).
  if (!wA.length !== !wB.length) return false
  const zawiera = (duzy, maly) => maly.every((x) => duzy.some((y) => Math.abs(x - y) <= tol))
  return zawiera(wA, wB) || zawiera(wB, wA)
}

/**
 * Pasma wymiarów z wierszy. Wiersz to punkty jednej cechy (rząd modułów, przeszkoda, połać) w kolejności
 * ważności. Wiersz trafia do pierwszego pasma, z którego punktami jest zgodny (`zgodnePunkty`); inaczej zakłada
 * nowe pasmo (najwyżej `maks`, nadmiar pomijamy: pełne wymiary są na arkuszach połaci). Każde pasmo dostaje
 * końce `zakres`, więc pierwszy odcinek to odległość od krawędzi.
 * @param wiersze [[m...]]  @param zakres [lo, hi]  @returns [[m...]] punkty pasm
 */
export const pasmaWymiarow = (wiersze, zakres, { maks = 4, tol = TOLERANCJA } = {}) => {
  const pasma = []
  for (const surowe of wiersze) {
    const punkty = punktyLancucha(surowe, tol)
    if (punkty.length < 2) continue
    const i = pasma.findIndex((ps) => zgodnePunkty(ps, punkty, tol))
    if (i >= 0) pasma[i] = punktyLancucha([...pasma[i], ...punkty], tol)
    else if (pasma.length < maks) pasma.push(punkty)
  }
  return pasma.map((ps) => punktyLancucha([...zakres, ...ps], tol))
}

/** Napisy wymiarów: wysokość cyfr 6,5 pt w mm i odstęp od linii (i między poziomami). */
export const TEKST_WYMIARU = { wysokosc: 1.7, odstep: 0.9 }

/** Grubość pasma w mm: napisy na poziomach 0..poziomy po jednej stronie linii i przerwa do następnego pasma. */
export const wysokoscPasma = (poziomy, przerwa = 2.5) => TEKST_WYMIARU.odstep + (poziomy + 1) * (TEKST_WYMIARU.wysokosc + TEKST_WYMIARU.odstep) + przerwa
